import assign from "object-assign"

import {
    BufferAttribute,
    BufferGeometry,
    Mesh,
    MeshLambertMaterial,
    DoubleSide,
    Plane,
    Quaternion,
    Vector3, VertexNormalsHelper, AddOperation, BoxGeometry, MeshPhongMaterial
} from "three"

import { TAU, rad } from "./util/math"
import Cannon from "cannon";
import isArray from "./util/is-array";
import { Promise } from "es6-promise-polyfill";
import callback from "./util/callback";
import { HOVER_HEIGHT } from "./x-racer";

const DEFAULTS = {

    geomResolution: 7,
    bodyResolution: 4
};

const UV_MIN = 2 * (1/512);
const UV_MAX = 254 * (1/512);

let trackTexture;

const PLATE_THICKNESS = 0.2;

const bodyMaterial = new MeshPhongMaterial({
    color: 0x000088
});


function quatFromOrientation(orientation, curQuat)
{
    if (!orientation)
    {
        return curQuat;
    }

    const quaternion = new Quaternion();
    quaternion.setFromAxisAngle(orientation.vector, orientation.angle);

    if (orientation.global)
    {
        return quaternion;
    }
    else
    {
        const quat = curQuat.clone();
        quat.multiply(quaternion);

        return quat;
    }

}

const xyPlane = new Plane().setFromNormalAndCoplanarPoint(new Vector3(0,0,1), new Vector3(0,0,0));
const forward = new Vector3(0,1,0);
const modelForward = new Vector3(0,1,0);
const forwardOnXY = new Vector3();

const TEXTURE_ARROW = { u: 0, v : 0};
const TEXTURE_TRACK = { u: 0.5, v : 0.5};
const TEXTURE_GOAL = { u: 0, v : 0.5};
const TEXTURE_SAFETY = { u: 0.5, v : 0};

function evaluate(orientation, curQuat)
{
    if (isArray(orientation))
    {
        for (let i = 0; i < orientation.length; i++)
        {
            curQuat = quatFromOrientation(orientation[i], curQuat);
        }
    }
    else
    {
        curQuat = quatFromOrientation(orientation, curQuat);
    }

    return curQuat;

}

function applyDefaults(data)
{
    let prevSegment = null;

    // handle defaults / inheritance
    const segmentCount = data.length;
    for (let i = 0; i < segmentCount; i++)
    {
        const segment = data[i];

        const position = segment.position;
        const goal = segment.goal;

        data[i] = assign({}, DEFAULTS, prevSegment, segment);
        data[i].position = position;
        data[i].goal = goal;

        prevSegment = data[i];
    }

    //console.log("applyDefaults", JSON.stringify(data, null, 2));
}

class Track
{
    constructor(data, ctx)
    {

        let x=0,y=0,z=0, quat = null, endQuat = null;
        applyDefaults(data);

        this.data = data;

        //console.log("SEGMENTS", data);

        this.positions = [];
        this.quats = [];
        this.xAxes = [];
        this.yAxes = [];
        this.zAxes = [];
        this.segments = [];
        this.zCorrect = [];
        this.vSteps = [];

        const segmentCount = data.length;
        const last = segmentCount - 1;

        let quatOff = 0;

        for (let i = 0; i < segmentCount; i++)
        {
            const segment = data[i];

            const { position, orientation, length } = segment;
            if (position)
            {
                x = position.x;
                y = position.y;
                z = position.z;
            }
            else
            {
                segment.position = {
                    x,y,z
                };
            }

            if (orientation)
            {
                quat = evaluate(orientation, quat);
            }

            if (i < last)
            {
                endQuat = evaluate(data[i+1].orientation, quat);
            }
            else
            {
                endQuat = quat;
            }

            const isSlerping = endQuat !== quat && !endQuat.equals(quat);

            const { radius, arc, geomResolution, bodyResolution} = segment;

            const goal = segment.goal < 0 ? geomResolution + segment.goal + 1 : segment.goal;

            let off = 0;

            // figure out the yResolution necessary for square tiles
            const yResolution = Math.floor(length / (arc * radius)) * geomResolution;
            console.log({geomResolution, yResolution});

            const vertices = new Float32Array((geomResolution-1) * (yResolution-1) * 18);
            const uvs = new Float32Array((geomResolution-1) * (yResolution-1) * 12);
            const normals = new Float32Array((geomResolution-1) * (yResolution-1) * 18);
            const angleStep = arc / (geomResolution - 1);

            const vStep = length / yResolution;

            for (let j=0; j < yResolution; j++)
            {
                const q = new Quaternion();
                if (isSlerping)
                {
                    Quaternion.slerp(quat, endQuat, q, j / (yResolution-1));
                    //console.log("Slerped", q);
                }
                else
                {
                    q.copy(quat);
                }

                this.xAxes[ quatOff + j] = new Vector3(
                    1,
                    0,
                    0
                ).applyQuaternion(q);

                this.yAxes[ quatOff + j] = new Vector3(
                    0,
                    1,
                    0
                ).applyQuaternion(q);

                this.zAxes[ quatOff + j] = new Vector3(
                    0,
                    0,
                    1
                ).applyQuaternion(q);

                this.quats[ quatOff + j] = q;

                this.positions[quatOff + j] = new Vector3(x,y,z);
                this.segments[quatOff + j] = i;

                modelForward.copy(forward).applyQuaternion(q);

                xyPlane.projectPoint(modelForward, forwardOnXY);
                // orientation

                this.zCorrect[quatOff + j] = forwardOnXY.angleTo(forward);
                this.vSteps[quatOff + j] = vStep;

                if (j < yResolution - 1)
                {
                    x += this.yAxes[ quatOff + j].x * vStep;
                    y += this.yAxes[ quatOff + j].y * vStep;
                    z += this.yAxes[ quatOff + j].z * vStep;
                }
            }


            const nextPos = new Vector3(x,y,z);

            //console.log({quats,xAxes,zAxes});

            //debugger;

            const startAngle = TAU/4 - arc/2;

            let uvOff = 0;

            // create geometry data
            for (let j=1; j < yResolution; j++)
            {
                let angle = startAngle;

                const position = this.positions[quatOff + j - 1];
                const nextPosition = this.positions[quatOff + j];
                const x = position.x;
                const y = position.y;
                const z = position.z;
                const nextX = nextPosition.x;
                const nextY = nextPosition.y;
                const nextZ = nextPosition.z;

                for (let k=1; k < geomResolution; k++)
                {
                    const nextAngle = angle + angleStep;

                    // +cos/-sin angle here ( zero degrees is to the right, clockwise, -z is down)
                    const cosFactor = Math.cos(angle) * radius;
                    const sinFactor = -Math.sin(angle)* radius;
                    const nextCosFactor = Math.cos(nextAngle) * radius;
                    const nextSinFactor = -Math.sin(nextAngle) * radius;

                    const x0 = cosFactor * this.xAxes[ quatOff + j - 1].x + sinFactor * this.zAxes[ quatOff + j - 1].x;
                    const y0 = cosFactor * this.xAxes[ quatOff + j - 1].y + sinFactor * this.zAxes[ quatOff + j - 1].y;
                    const z0 = cosFactor * this.xAxes[ quatOff + j - 1].z + sinFactor * this.zAxes[ quatOff + j - 1].z;

                    const x1 = nextCosFactor * this.xAxes[ quatOff + j - 1].x + nextSinFactor * this.zAxes[ quatOff + j - 1].x;
                    const y1 = nextCosFactor * this.xAxes[ quatOff + j - 1].y + nextSinFactor * this.zAxes[ quatOff + j - 1].y;
                    const z1 = nextCosFactor * this.xAxes[ quatOff + j - 1].z + nextSinFactor * this.zAxes[ quatOff + j - 1].z;

                    const x2 = cosFactor * this.xAxes[ quatOff + j].x + sinFactor * this.zAxes[ quatOff + j].x;
                    const y2 = cosFactor * this.xAxes[ quatOff + j].y + sinFactor * this.zAxes[ quatOff + j].y;
                    const z2 = cosFactor * this.xAxes[ quatOff + j].z + sinFactor * this.zAxes[ quatOff + j].z;

                    const x3 = nextCosFactor * this.xAxes[ quatOff + j].x + nextSinFactor * this.zAxes[ quatOff + j].x;
                    const y3 = nextCosFactor * this.xAxes[ quatOff + j].y + nextSinFactor * this.zAxes[ quatOff + j].y;
                    const z3 = nextCosFactor * this.xAxes[ quatOff + j].z + nextSinFactor * this.zAxes[ quatOff + j].z;

                    // three js mesh data
                    
                    vertices[off++] = x + x0;
                    vertices[off++] = y + y0;
                    vertices[off++] = z + z0;
                    vertices[off++] = nextX + x2;
                    vertices[off++] = nextY + y2;
                    vertices[off++] = nextZ + z2;
                    vertices[off++] = x + x1;
                    vertices[off++] = y + y1;
                    vertices[off++] = z + z1;

                    vertices[off++] = nextX + x2;
                    vertices[off++] = nextY + y2;
                    vertices[off++] = nextZ + z2;
                    vertices[off++] = nextX + x3;
                    vertices[off++] = nextY + y3;
                    vertices[off++] = nextZ + z3;
                    vertices[off++] = x + x1;
                    vertices[off++] = y + y1;
                    vertices[off++] = z + z1;

                    off -=18;

                    normals[off++] = x0 / -radius;
                    normals[off++] = y0 / -radius;
                    normals[off++] = z0 / -radius;
                    normals[off++] = x2 / -radius;
                    normals[off++] = y2 / -radius;
                    normals[off++] = z2 / -radius;
                    normals[off++] = x1 / -radius;
                    normals[off++] = y1 / -radius;
                    normals[off++] = z1 / -radius;

                    normals[off++] = x2 / -radius;
                    normals[off++] = y2 / -radius;
                    normals[off++] = z2 / -radius;
                    normals[off++] = x3 / -radius;
                    normals[off++] = y3 / -radius;
                    normals[off++] = z3 / -radius;
                    normals[off++] = x1 / -radius;
                    normals[off++] = y1 / -radius;
                    normals[off++] = z1 / -radius;


                    const texture = goal === j ? TEXTURE_GOAL : TEXTURE_TRACK;

                    uvs[uvOff++] = texture.u + UV_MIN;
                    uvs[uvOff++] = texture.v + UV_MIN;
                    uvs[uvOff++] = texture.u + UV_MIN;
                    uvs[uvOff++] = texture.v + UV_MAX;
                    uvs[uvOff++] = texture.u + UV_MAX;
                    uvs[uvOff++] = texture.v + UV_MIN;

                    uvs[uvOff++] = texture.u + UV_MIN;
                    uvs[uvOff++] = texture.v + UV_MAX;
                    uvs[uvOff++] = texture.u + UV_MAX;
                    uvs[uvOff++] = texture.v + UV_MAX;
                    uvs[uvOff++] = texture.u + UV_MAX;
                    uvs[uvOff++] = texture.v + UV_MIN;

                    // cannon.js collision geometry
                    

                    const sideLength = Math.sin(angleStep) / Math.sin((TAU/2 - angleStep)/2) * radius;

                    const shape = new Cannon.Box(new Cannon.Vec3(
                        PLATE_THICKNESS,
                        sideLength,
                        sideLength
                    ));
                    const body = new Cannon.Body({
                        mass: 0,
                        material: TRACK_MATERIAL
                    });
                    body.addShape(shape);

                    const midPos = this.positions[quatOff + j - 1].clone().add(this.positions[quatOff + j]).multiplyScalar(0.5);

                    const midQuat = this.quats[quatOff + j - 1].clone().slerp(this.quats[quatOff + j], 0.5);


                    const xAxis = new Vector3(
                        1,
                        0,
                        0
                    ).applyQuaternion(midQuat);

                    const yAxis = new Vector3(
                        0,
                        1,
                        0
                    ).applyQuaternion(midQuat);

                    const zAxis = new Vector3(
                        0,
                        0,
                        1
                    ).applyQuaternion(midQuat);


                    const midAngle = (angle + nextAngle) / 2;
                    const rotateArc = new Quaternion().setFromAxisAngle(yAxis, midAngle)

                    midQuat.premultiply(rotateArc);

                    const cosFactor2 = Math.cos(midAngle) * radius;
                    const sinFactor2 = -Math.sin(midAngle)* radius;

                    midPos.x += cosFactor2 * xAxis.x + sinFactor2 * zAxis.x;
                    midPos.y += cosFactor2 * xAxis.y + sinFactor2 * zAxis.y;
                    midPos.z += cosFactor2 * xAxis.z + sinFactor2 * zAxis.z;

                    body.position.copy(midPos);
                    body.quaternion.copy(midQuat);
                    ctx.world.addBody(body);

 
                    angle = nextAngle;
                }
            }




            //console.log("TRACK", vertices);
            const geometry = new BufferGeometry();
            geometry.addAttribute( "position", new BufferAttribute( vertices, 3 ) );
            geometry.addAttribute( "normal", new BufferAttribute( normals, 3 ) );
            geometry.addAttribute( 'uv', new BufferAttribute( uvs, 2 ) );

            geometry.computeBoundingSphere();

            const mesh = new Mesh(
                geometry,
                new MeshLambertMaterial({
                    color: 0x353b42,
                    transparent: true,
                    opacity: 0.8,
                    map: trackTexture,
                    side: DoubleSide
                }));
            //mesh.quaternion.setFromAxisAngle(new Vector3(0,1,0), TAU/4);
            ctx.scene.add(mesh);

            // const helper = new VertexNormalsHelper( mesh, 0.5, 0x00ff00, 1 );
            // ctx.scene.add(helper);

            geometry.computeBoundingBox();
            //console.log("Track AABB", JSON.stringify(geometry.boundingBox));

            quatOff += yResolution - 1;
            x = nextPos.x;
            y = nextPos.y;
            z = nextPos.z;
        }

        this.length = quatOff;
    }


    getStartPosition()
    {
        const pos = this.positions[0];
        const segment = this.data[this.segments[0]];
        const yAxis = this.yAxes[0].clone();
        const zAxis = this.zAxes[0].clone();

        return zAxis.multiplyScalar( -(segment.radius - HOVER_HEIGHT * 1.1) ).add(yAxis.multiplyScalar(1)).add(pos);
    }
    
    static load(loader)
    {
        return callback(done => loader.load("blender/track-texture.png", done))
            .then(texture => {
                trackTexture = texture[0];
            });
    }
}

export const TRACK_MATERIAL = new Cannon.Material("TRACK");


export default Track



// WEBPACK FOOTER //
// ./src/track.js